/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * WHEN THE FATF LISTS ARE OUT OF DATE (v1_881).
 *
 * Until v1_881 the lists counted as out of date from the first day of a
 * plenary month (1 February, 1 June, 1 October). FATF publishes its lists on
 * the last day of the plenary, usually late in the month, and the previous
 * lists stay FATF's current lists until then. So for about three weeks every
 * assessment told the officer the lists were out of date when they were not.
 *
 * Now the lists count as out of date only once a newer publication was due
 * and has not been loaded: the expected publication date has passed (with two
 * working days to load the new lists) and the lists in the build are dated
 * before it. The expected date is recorded when the lists are loaded (FATF
 * announces its plenary dates in advance). Where none is recorded, the last
 * day of the next plenary month is used, which is never earlier than the real
 * publication. The state is always returned with its basis, so the officer
 * sees whether the lists are current and when the next update is expected.
 *
 * Shared by the engine (api/_country-lists.js) and the dashboard. Pure; dates
 * are ISO strings internally and dd/mm/yyyy in anything a person reads.
 */

// The lists in this build. Update together with the arrays in
// api/_country-lists.js, from the FATF website only.
export const FATF_AS_OF = '2026-06-19';
export const FATF_SOURCE = 'FATF Plenary, Paris, 17 to 19 June 2026';

// The next expected publication, recorded when the lists above are loaded:
// { date: 'yyyy-mm-dd', plenary: 'FATF Plenary, <place>, <dates>' }, or null
// when FATF's announced date has not been recorded (the end of the next
// plenary month is then used, and the screen says so).
export const FATF_NEXT_PUBLICATION = null;

const PLENARY_MONTHS = [1, 5, 9]; // February, June, October (0 based)
const GRACE_WORKING_DAYS = 2;

const isoDay = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
};

// dd/mm/yyyy for anything a person reads; '' for a missing or bad date.
export function ukDate(iso) {
  const d = parseIso(iso);
  return d ? isoDay(d).split('-').reverse().join('/') : '';
}

// The last day of the first plenary month after the lists' own month.
export function endOfNextPlenaryMonth(asOfIso) {
  const a = parseIso(asOfIso);
  if (!a) return null;
  const y = a.getUTCFullYear(), m = a.getUTCMonth();
  const next = PLENARY_MONTHS.find(pm => pm > m);
  const ny = next === undefined ? y + 1 : y;
  const nm = next === undefined ? PLENARY_MONTHS[0] : next;
  return isoDay(new Date(Date.UTC(ny, nm + 1, 0)));
}

function addWorkingDays(iso, n) {
  const d = parseIso(iso);
  let left = n;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const wd = d.getUTCDay();
    if (wd !== 0 && wd !== 6) left--;
  }
  return isoDay(d);
}

/**
 * The state of the lists on a given day. Never throws.
 *   current       true while no newer publication is overdue
 *   stale         the inverse, for the engine's data quality notice
 *   nextExpected  the expected publication date (ISO)
 *   basis         'recorded' (FATF's announced date) or 'end of plenary month'
 *   staleFrom     the first day the lists count as out of date (ISO)
 */
export function fatfListStatus(now = new Date(), asOf = FATF_AS_OF, next = FATF_NEXT_PUBLICATION) {
  const recorded = next && parseIso(next.date) ? next.date : null;
  const nextExpected = recorded || endOfNextPlenaryMonth(asOf);
  if (!nextExpected || !parseIso(asOf)) {
    return { asOf, current: false, stale: true, nextExpected: null, basis: 'unknown', staleFrom: null, plenary: null };
  }
  const staleFrom = addWorkingDays(nextExpected, GRACE_WORKING_DAYS + 1);
  const today = isoDay(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));
  const stale = asOf < nextExpected && today >= staleFrom;
  return {
    asOf,
    current: !stale,
    stale,
    nextExpected,
    basis: recorded ? 'recorded' : 'end of plenary month',
    staleFrom,
    plenary: recorded ? (next.plenary || null) : null
  };
}

// The line the officer reads beside the tier. No dashes.
export function fatfListStatusLine(status) {
  const s = status || fatfListStatus();
  const dated = ukDate(s.asOf);
  if (!s.nextExpected) return 'FATF lists dated ' + (dated || 'unknown') + '. The date of the next update is not known; confirm the current lists on the FATF website.';
  const when = ukDate(s.nextExpected) + (s.basis === 'recorded'
    ? (s.plenary ? ' (' + s.plenary + ')' : '')
    : ' (the end of the plenary month; FATF\'s announced date is not recorded)');
  return s.current
    ? 'FATF lists dated ' + dated + ', the current lists. Next update expected by ' + when + '.'
    : 'FATF lists dated ' + dated + ' are out of date: newer lists were expected by ' + when + ' and have not been loaded.';
}
